import CertificationLayout from '../../layouts/CertificationLayout';
import { AffiliateCTA } from '../../components/content/AffiliateCTA';
import { ResourceLink } from '../../components/content/ResourceLink';
import { CalculatorEmbed } from '../../components/content/CalculatorEmbed';
import type { ExamDetails } from '../../layouts/CertificationLayout';
import type { TocItem, RelatedCalculator } from '../../layouts/GuideLayout';

const examDetails: ExamDetails = {
  examCode: 'SC-200',
  examName: 'Microsoft Security Operations Analyst',
  passingScore: 700,
  examCostGBP: 165,
  skillsMeasured: [
    'Mitigate threats using Microsoft Defender XDR (20–25%)',
    'Mitigate threats using Microsoft Defender for Cloud (25–30%)',
    'Mitigate threats using Microsoft Sentinel (50–55%)',
  ],
};

const tocItems: TocItem[] = [
  { id: 'what-is-sc200', label: 'What is SC-200?', level: 2 },
  { id: 'exam-topics', label: 'Exam Topic Breakdown', level: 2 },
  { id: 'uk-context', label: 'UK Context', level: 2 },
  { id: 'free-resources', label: 'Free Study Resources', level: 2 },
  { id: 'paid-resources', label: 'Paid Study Resources', level: 2 },
  { id: 'study-plan', label: 'Recommended Study Plan', level: 2 },
  { id: 'booking', label: 'Booking the Exam in the UK', level: 2 },
  { id: 'related-resources', label: 'Related Resources', level: 2 },
];

const relatedCalculators: RelatedCalculator[] = [
  {
    service: 'log-analytics',
    path: '/log-analytics',
    label: 'Log Analytics & Sentinel Calculator',
  },
];

const topCta = (
  <AffiliateCTA
    programme="Pluralsight"
    courseName="SC-200 Learning Path"
    url="https://www.pluralsight.com/paths/sc-200-microsoft-security-operations-analyst-associate"
    price="From £29/month"
    badgeText="Most Comprehensive"
  />
);

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'SC-200 Study Guide UK 2026',
  description:
    'Complete SC-200 study guide for UK engineers. Exam structure, topic breakdown, free resources, and the best paid courses. Updated March 2026.',
  author: {
    '@type': 'Organization',
    name: 'AzureCalc.uk',
  },
  publisher: {
    '@type': 'Organization',
    name: 'AzureCalc.uk',
    url: 'https://www.azure-calc.co.uk',
  },
  dateModified: '2026-03-30',
  url: 'https://www.azure-calc.co.uk/certifications/sc-200-study-guide-uk',
};

export default function SC200StudyGuidePage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <CertificationLayout
        title="SC-200 Study Guide UK 2026 — Microsoft Security Operations Analyst"
        description="Complete SC-200 study guide for UK engineers. Exam structure, topic breakdown, free resources, and the best paid courses. Updated March 2026."
        lastUpdated="March 2026"
        tocItems={tocItems}
        examDetails={examDetails}
        relatedCalculators={relatedCalculators}
        topCta={topCta}
        bottomCta={
          <AffiliateCTA
            programme="MeasureUp"
            courseName="SC-200 Official Practice Test"
            url="https://www.measureup.com/microsoft-practice-test-sc-200-microsoft-security-operations-analyst.html"
            price="From £99"
            badgeText="Official Microsoft Partner"
          />
        }
        canonical="https://www.azure-calc.co.uk/certifications/sc-200-study-guide-uk"
        metaTitle="SC-200 Study Guide UK 2026 | AzureCalc.uk"
        metaDescription="Complete SC-200 Microsoft Security Operations Analyst study guide for UK engineers. Exam domains, 6-week study plan, free and paid resources."
      >
        {/* ── Section 1: What is SC-200? ── */}
        <section id="what-is-sc200" className="mb-10">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>What is SC-200?</h2>
          <p className="text-[15px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            The SC-200 — Microsoft Security Operations Analyst — is Microsoft's certification for
            security professionals who work in a Security Operations Centre (SOC) environment. It
            validates your ability to detect, investigate, and respond to threats using the Microsoft
            security stack: Microsoft Sentinel, Microsoft Defender XDR, and Microsoft Defender for
            Cloud. It is a role-based certification aimed at practising SOC analysts, security
            engineers, and threat hunters who work day-to-day with Microsoft security tooling.
          </p>
          <p className="text-[15px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            In the UK, SC-200 has become an increasingly sought-after credential in financial
            services, government, and NHS organisations that have adopted Microsoft Sentinel as their
            SIEM platform. FCA-regulated firms in particular are driving demand: as Sentinel
            deployments mature, organisations need analysts who can configure detection rules, write
            KQL queries, and manage incident workflows — all core SC-200 exam objectives. If you are
            already working with the Microsoft security stack, this certification directly validates
            those skills in a way that AZ-500 (more architecture-focused) does not.
          </p>
          <p className="text-[15px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            The exam consists of 40–60 questions covering multiple formats: multiple choice,
            drag-and-drop scenario tasks, and case studies. There is no mandatory prerequisite, but
            Microsoft recommends familiarity with Azure fundamentals — AZ-900 is a useful foundation
            if you are new to the Azure platform. The passing score is 700 out of 1000, and the exam
            costs £165 when booked through Pearson VUE in the UK.
          </p>
        </section>

        {/* ── Section 2: Exam Topic Breakdown ── */}
        <section id="exam-topics" className="mb-10">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Exam Topic Breakdown</h2>
          <p className="text-[14px] leading-relaxed mb-6" style={{ color: 'var(--text-tertiary)' }}>
            The SC-200 exam is divided into three domains. The weighting below is taken from
            Microsoft's official study guide and is approximate — individual exams may vary within
            these ranges.
          </p>

          {/* Domain 1 */}
          <div className="mb-6 rounded-lg p-5" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[16px] font-bold" style={{ color: 'var(--text-primary)' }}>
                Domain 1: Mitigate threats using Microsoft Defender XDR
              </h3>
              <span className="shrink-0 text-[12px] font-bold text-[#0078d4] px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid #0078d4' }}>
                20–25%
              </span>
            </div>
            <ul className="flex flex-col gap-1.5">
              {[
                'Defender for Endpoint — device onboarding, threat detection, and response',
                'Defender for Office 365 — email threat protection, Safe Links, Safe Attachments',
                'Defender for Identity — Active Directory threat detection and lateral movement',
                'Defender for Cloud Apps — shadow IT discovery, session policies, app governance',
                'Microsoft Defender portal — unified incident queue and cross-product investigation',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                  <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Domain 2 */}
          <div className="mb-6 rounded-lg p-5" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[16px] font-bold" style={{ color: 'var(--text-primary)' }}>
                Domain 2: Mitigate threats using Microsoft Defender for Cloud
              </h3>
              <span className="shrink-0 text-[12px] font-bold text-[#0078d4] px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid #0078d4' }}>
                25–30%
              </span>
            </div>
            <ul className="flex flex-col gap-1.5">
              {[
                'Security posture management — Secure Score, recommendations, cloud security graph',
                'Workload protection — Microsoft Defender plans for VMs, containers, databases',
                'Regulatory compliance — built-in frameworks, custom policy assignments',
                'Defender for DevOps — code security posture, IaC scanning, GitHub/ADO integration',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                  <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Domain 3 */}
          <div className="mb-4 rounded-lg p-5" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid rgba(0, 120, 212, 0.5)' }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[16px] font-bold" style={{ color: 'var(--text-primary)' }}>
                Domain 3: Mitigate threats using Microsoft Sentinel
              </h3>
              <span className="shrink-0 text-[12px] font-bold text-[#0078d4] px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid #0078d4' }}>
                50–55%
              </span>
            </div>
            <ul className="flex flex-col gap-1.5 mb-4">
              {[
                'Workspace architecture — Log Analytics workspace design, cost management, RBAC',
                'Data connectors and ingestion — Microsoft, partner, and custom connectors',
                'KQL query writing — hunting queries, workbooks, detection rule logic',
                'Analytics rules — scheduled rules, NRT rules, ML-based UEBA anomaly detection',
                'Incident management — investigation, entity pages, investigation graph',
                'SOAR — playbooks (Logic Apps), automation rules, Microsoft Sentinel orchestration',
                'Threat intelligence — TAXII feeds, upload indicators, MITRE ATT&CK framework mapping',
                'Content hub — solutions, standalone items, community contributions',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                  <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                  {item}
                </li>
              ))}
            </ul>
            <div className="flex items-start gap-2 rounded p-3" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid rgba(0, 120, 212, 0.3)' }}>
              <span className="material-symbols-outlined text-[#0078d4] text-[16px] shrink-0 mt-0.5">
                info
              </span>
              <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
                <strong style={{ color: 'var(--text-primary)' }}>Domain 3 carries more than half the exam.</strong>{' '}
                Engineers who work with Microsoft Sentinel daily — writing KQL, managing analytics
                rules, investigating incidents — have a significant structural advantage. If you are
                new to Sentinel, allocate at least half your study time here.
              </p>
            </div>
          </div>
        </section>

        {/* ── Section 3: UK Context ── */}
        <section id="uk-context" className="mb-10">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>UK Context</h2>
          <p className="text-[15px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            In the UK job market, SC-200 is increasingly valued alongside the broader Sentinel
            ecosystem. Typical salary ranges for UK SOC analysts and security engineers holding
            SC-200 are <strong style={{ color: 'var(--text-primary)' }}>£45,000–£75,000</strong>, with senior
            threat hunters and detection engineers in London-based financial services firms reaching
            £80,000+. The certification is particularly relevant to roles in banking, insurance, and
            asset management firms that have chosen Microsoft Sentinel as their primary SIEM — a
            pattern that has accelerated since FCA guidance on operational resilience encouraged
            firms to consolidate their security tooling.
          </p>
          <p className="text-[15px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            NHS England and NHS Scotland have significant Sentinel deployments under the NHS Cyber
            Security programme. Public sector roles requiring SC-200 or equivalent Sentinel
            experience are increasingly common in central government and NCSC-aligned organisations.
            UK defence contractors also commonly list Sentinel experience as a requirement for SOC
            roles.
          </p>

          <div className="rounded-lg p-5 flex flex-col gap-3" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <h3 className="text-[15px] font-bold" style={{ color: 'var(--text-primary)' }}>Practical exam information for UK candidates</h3>
            <ul className="flex flex-col gap-2">
              {[
                'Book via Pearson VUE at pearsonvue.com/microsoft — online proctored or test centre',
                'Test centres available in London, Manchester, Birmingham, Edinburgh, and Bristol',
                'Exam is English-only — no Welsh language option is currently available',
                'Online proctored: requires a quiet room, working webcam, and a cleared desk',
                'Many UK employers will fund SC-200 as continuing professional development (CPD) — check your L&D budget before paying personally',
                'Microsoft exam vouchers occasionally available through Microsoft Learn Challenges and ESI agreements',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                  <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ── Calculator cross-link after UK Context ── */}
        <div className="mb-10">
          <p className="text-[14px] mb-3" style={{ color: 'var(--text-tertiary)' }}>
            Before deploying Sentinel, estimate your Log Analytics costs:
          </p>
          <CalculatorEmbed service="log-analytics" />
        </div>

        {/* ── Section 4: Free Resources ── */}
        <section id="free-resources" className="mb-10">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Free Study Resources</h2>
          <p className="text-[15px] leading-relaxed mb-5" style={{ color: 'var(--text-secondary)' }}>
            There is a solid set of free resources for SC-200, anchored by Microsoft's own learning
            platform. The main limitation is the absence of exam-quality practice questions — free
            resources will build your knowledge but won't benchmark your exam readiness.
          </p>

          <div className="flex flex-col gap-3 mb-5">
            {[
              {
                title: 'Official SC-200 learning path on Microsoft Learn',
                url: 'https://learn.microsoft.com/en-us/training/paths/sc-200-mitigate-threats-using-microsoft-defender/',
                description: 'The authoritative free resource. Covers all three exam domains with structured modules, knowledge checks, and sandbox exercises.',
              },
              {
                title: 'SC-200 study guide (official PDF from Microsoft)',
                url: 'https://learn.microsoft.com/en-us/credentials/certifications/resources/study-guides/sc-200',
                description: 'Lists every exam objective with links to the relevant Microsoft Learn modules. Use this as your exam checklist.',
              },
              {
                title: 'Microsoft Sentinel GitHub repository',
                url: 'https://github.com/Azure/Azure-Sentinel',
                description: 'Community detection rules, workbooks, and playbooks. Reviewing real detection logic is an excellent way to prepare for the KQL and analytics rules questions.',
              },
              {
                title: "John Savill's SC-200 YouTube series",
                url: 'https://www.youtube.com/@NTFAQGuy',
                description: 'John Savill is widely regarded as the best free Azure certification resource. His SC-200 coverage is thorough and explains the why behind each feature, not just the what.',
              },
              {
                title: 'Microsoft Security Community blog',
                url: 'https://techcommunity.microsoft.com/category/microsoft-sentinel',
                description: 'Real-world Sentinel deployment posts from Microsoft engineers and MVPs. Useful for understanding current product features and exam-relevant architecture patterns.',
              },
            ].map((item) => (
              <div key={item.title} className="rounded-lg p-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[14px] font-semibold text-[#0078d4] hover:underline transition-colors flex items-center gap-1.5 mb-1"
                >
                  {item.title}
                  <span className="material-symbols-outlined text-[14px]" style={{ color: 'var(--text-tertiary)' }}>open_in_new</span>
                </a>
                <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>{item.description}</p>
              </div>
            ))}
          </div>

          <div className="flex items-start gap-3 rounded-lg p-4 bg-[#e8b000]/10 border border-[#e8b000]/40">
            <span className="material-symbols-outlined text-[#e8b000] text-[18px] shrink-0 mt-0.5">warning</span>
            <div>
              <p className="text-[13px] font-semibold text-[#e8b000] mb-1">Limitations of free resources</p>
              <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
                Free resources do not include realistic practice exam questions — the most effective
                way to identify weak areas before the real exam. Without a practice test, you may
                pass knowledge checks on Microsoft Learn while still being underprepared for the
                scenario-based questions. If you are serious about passing first time, combine free
                resources with at least one paid practice test (see section below).
              </p>
            </div>
          </div>
        </section>

        {/* ── Section 5: Paid Resources ── */}
        <section id="paid-resources" className="mb-10">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Paid Study Resources</h2>
          <p className="text-[15px] leading-relaxed mb-5" style={{ color: 'var(--text-secondary)' }}>
            The three resources below cover the full preparation spectrum: deep video learning,
            official practice exams, and a budget question bank. Most candidates use a combination
            of two: a video course for knowledge building, and a practice test for exam readiness
            assessment.
          </p>

          <div className="flex flex-col gap-4">
            <AffiliateCTA
              programme="Pluralsight"
              courseName="SC-200 Learning Path"
              url="https://www.pluralsight.com/paths/sc-200-microsoft-security-operations-analyst-associate"
              price="From £29/month"
              badgeText="Most Comprehensive"
            />

            <div className="border rounded-lg p-4 text-[13px] leading-relaxed" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)', color: 'var(--text-tertiary)' }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Why Pluralsight for SC-200:</strong> The SC-200
              learning path has the most comprehensive video coverage of the three exam domains. The
              Sentinel modules are particularly strong — they cover KQL writing, analytics rule
              configuration, and SOAR automation in depth that goes beyond what Microsoft Learn
              provides. Hands-on labs let you practice in a live Sentinel environment without needing
              your own Azure subscription. The skill assessment feature identifies weak areas
              before you sit the exam.
            </div>

            <AffiliateCTA
              programme="MeasureUp"
              courseName="SC-200 Official Practice Test"
              url="https://www.measureup.com/microsoft-practice-test-sc-200-microsoft-security-operations-analyst.html"
              price="From £99"
              badgeText="Official Microsoft Partner"
            />

            <div className="border rounded-lg p-4 text-[13px] leading-relaxed" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)', color: 'var(--text-tertiary)' }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Why MeasureUp for exam prep:</strong> MeasureUp is
              an official Microsoft learning partner, and their practice tests use the same question
              format and difficulty calibration as the real SC-200 exam. If you can consistently
              score 75%+ on a MeasureUp practice test, you are ready to book. This is the most
              accurate pre-exam readiness signal available and is widely recommended by candidates
              who have passed.
            </div>

            <AffiliateCTA
              programme="Whizlabs"
              courseName="SC-200 Practice Tests"
              url="https://www.whizlabs.com/microsoft-azure-certification-sc-200/"
              price="From £15"
              badgeText="Budget Option"
            />

            <div className="border rounded-lg p-4 text-[13px] leading-relaxed" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)', color: 'var(--text-tertiary)' }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Why Whizlabs:</strong> At a fraction of the cost
              of MeasureUp, Whizlabs provides a large question bank that is useful for additional
              practice once you have worked through your primary study materials. The questions are
              not calibrated to the same standard as MeasureUp, but the volume makes it a good
              supplement for reinforcing knowledge across all three domains before your exam date.
            </div>
          </div>
        </section>

        {/* ── Section 6: Study Plan ── */}
        <section id="study-plan" className="mb-10">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Recommended Study Plan</h2>
          <p className="text-[15px] leading-relaxed mb-5" style={{ color: 'var(--text-secondary)' }}>
            The following 6-week plan assumes part-time study of 1–2 hours per day. If you are
            already working with Microsoft Sentinel daily, compress Weeks 4–5 and use that time for
            additional practice exam runs.
          </p>

          <div className="flex flex-col gap-3">
            {[
              {
                week: 'Weeks 1–2',
                domain: 'Microsoft Defender XDR (Domain 1)',
                colour: 'border-[#0078d4]',
                items: [
                  'Complete the Defender XDR modules on Microsoft Learn',
                  'Focus on Defender for Endpoint — the most heavily tested sub-topic in Domain 1',
                  'Practice navigating the Microsoft Defender portal in a trial tenant',
                  'Understand the difference between Defender for Identity and Entra ID Protection',
                ],
              },
              {
                week: 'Week 3',
                domain: 'Microsoft Defender for Cloud (Domain 2)',
                colour: 'border-[#0078d4]',
                items: [
                  'Work through the Defender for Cloud modules on Microsoft Learn',
                  'Understand Secure Score — how recommendations are weighted and remediated',
                  'Review regulatory compliance dashboard — NIST, CIS, PCI-DSS framework mappings',
                  'Defender for DevOps is new — do not skip it, it appears in current exams',
                ],
              },
              {
                week: 'Weeks 4–5',
                domain: 'Microsoft Sentinel (Domain 3) — the heaviest domain',
                colour: 'border-[#107c10]',
                items: [
                  'This domain carries 50–55% of the exam — give it the most time',
                  'Set up a free Sentinel trial workspace and practice hands-on',
                  'Write KQL queries daily: summarize, join, extend, project, where, parse',
                  'Build at least one scheduled analytics rule and one NRT rule from scratch',
                  'Configure a Logic Apps playbook and attach it to an automation rule',
                  'Review the MITRE ATT&CK framework mapping in the threat intelligence blade',
                ],
              },
              {
                week: 'Week 6',
                domain: 'Practice exams and revision',
                colour: 'border-[#e8b000]',
                items: [
                  'Take a full MeasureUp practice test under exam conditions (timed, no notes)',
                  'Review every incorrect answer — read the explanation and the linked Microsoft Learn module',
                  'Re-read the Microsoft Learn modules for your two weakest sub-topics',
                  'Take a second MeasureUp practice test to confirm readiness',
                  'Book your exam if consistently scoring 75%+ on practice tests',
                ],
              },
            ].map(({ week, domain, colour, items }) => (
              <div key={week} className={`border-l-2 ${colour} border-t border-r border-b rounded-lg p-5`} style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="text-[11px] font-bold uppercase tracking-widest shrink-0" style={{ color: 'var(--text-tertiary)' }}>
                    {week}
                  </span>
                  <h3 className="text-[15px] font-bold" style={{ color: '#c7e0f4' }}>{domain}</h3>
                </div>
                <ul className="flex flex-col gap-1.5">
                  {items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                      <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* ── Section 7: Booking the Exam ── */}
        <section id="booking" className="mb-10">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Booking the Exam in the UK</h2>
          <p className="text-[15px] leading-relaxed mb-5" style={{ color: 'var(--text-secondary)' }}>
            SC-200 is delivered exclusively through Pearson VUE. You can choose between an online
            proctored exam (from home) or an in-person test centre sitting.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            {[
              {
                icon: 'laptop',
                title: 'Online Proctored',
                items: [
                  'Sit the exam from any quiet room with a working webcam',
                  'Desk must be cleared — no notes, second monitors, or phones',
                  'Requires reliable internet connection (minimum 1 Mbps up/down)',
                  'Available 24/7 — no need to travel to a test centre',
                ],
              },
              {
                icon: 'location_on',
                title: 'UK Test Centres',
                items: [
                  'London (multiple locations — City and West End)',
                  'Manchester, Birmingham, Edinburgh, Bristol',
                  'Book specific centres at pearsonvue.com/microsoft',
                  'Arrives 15 minutes early required — bring two forms of ID',
                ],
              },
            ].map(({ icon, title, items }) => (
              <div key={title} className="rounded-lg p-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="material-symbols-outlined text-[#0078d4] text-[20px]">{icon}</span>
                  <h3 className="text-[14px] font-bold" style={{ color: '#c7e0f4' }}>{title}</h3>
                </div>
                <ul className="flex flex-col gap-1.5">
                  {items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
                      <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="rounded-lg p-5 flex flex-col gap-2" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <h3 className="text-[14px] font-bold mb-1" style={{ color: '#c7e0f4' }}>Reschedule and retake policy</h3>
            {[
              'Reschedule or cancel for free up to 6 business days before your exam date',
              'Cancellation within 6 business days forfeits the full exam fee (£165)',
              'If you do not pass: wait 24 hours before rebooking for your second attempt',
              'Microsoft Exam Replay — purchase a retake voucher at a discount at the time of booking',
              'A second retake requires a 14-day wait; a third or subsequent attempt requires 14 days and can only be taken twice per 12-month period',
            ].map((item) => (
              <div key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                {item}
              </div>
            ))}
          </div>
        </section>

        {/* ── Section 8: Related Resources ── */}
        <section id="related-resources" className="mb-6">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Related Resources</h2>
          <div className="flex flex-col gap-3">
            <ResourceLink
              title="Official SC-200 certification page"
              url="https://learn.microsoft.com/en-us/credentials/certifications/security-operations-analyst/"
              source="Microsoft Learn"
            />
            <ResourceLink
              title="SC-200 study guide (official)"
              url="https://learn.microsoft.com/en-us/credentials/certifications/resources/study-guides/sc-200"
              source="Microsoft Learn"
            />
            <ResourceLink
              title="Microsoft Sentinel pricing UK — guide"
              url="/guides/azure-log-analytics-pricing-uk"
              source="AzureCalc.uk"
            />
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--text-tertiary)' }}>
                AzureCalc.uk
              </p>
              <CalculatorEmbed service="log-analytics" />
            </div>
          </div>
        </section>
      </CertificationLayout>
    </>
  );
}
